// src/lib/cleanup.js — reference-and-delete cleanup engine (§4)
//
// There is no blob-delete API. For each orphan CID, serially:
//   1. createRecord in the tool-owned temp collection (TID rkey, minimal shape)
//   2. deleteRecord for that record
//   3. the PDS garbage-collects the blob when its last reference disappears
//
// Never report success from a deleteRecord 200 alone — every deletion is
// re-verified against listBlobs. Outcomes per CID:
//   deleted        — verified gone from listBlobs
//   still-present  — mechanism didn't work on this PDS → STOP the run
//   undeletable    — create was rejected even with validate:false
//   already-absent — getBlob 404'd before we started → no mechanism needed

import { RateLimitTracker } from './rateLimit';

export const TEMP_NSID = 'com.psingletary.sifter.tempRef';

/**
 * Recover blob metadata (mimeType, size) via getBlob response headers.
 * @param {Function} fetchBlobHeaders async ({did, cid}) => {mimeType, size} | null
 * @param {{did: string, cid: string}} blob
 * @returns {Promise<{mimeType: string, size: number}>}
 */
export async function recoverBlobMeta(fetchBlobHeaders, { did, cid }) {
  try {
    const meta = await fetchBlobHeaders({ did, cid });

    if (meta) {
      return {
        mimeType: meta.mimeType || 'application/octet-stream',
        size: Number.isFinite(meta.size) ? meta.size : 0,
      };
    }
  } catch {
    // fall through to defaults; caller distinguishes 404 elsewhere
  }

  return { mimeType: 'application/octet-stream', size: 0 };
}

/**
 * Run the reference-and-delete mechanism for ONE orphan CID.
 *
 * @param {Object} opts
 * @param {{did: string, cid: string}} opts.blob
 * @param {Object} opts.meta             recovered {mimeType, size}
 * @param {Function} opts.createRecord   async ({repo, collection, record, validate}) => {data:{uri,rkey}}
 * @param {Function} opts.deleteRecord   async ({repo, collection, rkey}) => {}
 * @param {Function} opts.listBlobs      async ({did, limit, cursor}) => {data:{cids:[],cursor}}
 * @param {Function} opts.fetchBlobHeaders async ({did, cid}) => {mimeType, size} | null (throws on 404)
 * @returns {Promise<{outcome: 'deleted'|'still-present'|'undeletable'|'already-absent', detail?: string}>}
 */
export async function cleanupOneBlob({
  blob,
  meta,
  createRecord,
  deleteRecord,
  listBlobs,
  fetchBlobHeaders,
}) {
  const { did, cid } = blob;

  // If the blob is already gone, nothing to do.
  let goneBefore = false;

  try {
    await fetchBlobHeaders({ did, cid });
  } catch {
    // 404 → already absent
    goneBefore = true;
  }

  if (goneBefore) {
    return { outcome: 'already-absent', detail: 'getBlob 404 before mechanism' };
  }

  const record = {
    $type: TEMP_NSID,
    blob: {
      $type: 'blob',
      ref: { $link: cid },
      mimeType: meta.mimeType,
      size: meta.size,
    },
    createdAt: new Date().toISOString(),
  };

  // create with validate default; retry once with validate:false if rejected
  let createRes;

  try {
    createRes = await createRecord({ repo: did, collection: TEMP_NSID, record });
  } catch {
    try {
      createRes = await createRecord({ repo: did, collection: TEMP_NSID, record, validate: false });
    } catch (err2) {
      return { outcome: 'undeletable', detail: `createRecord rejected: ${err2?.message || err2}` };
    }
  }

  const rkey = createRes?.data?.rkey;

  if (!rkey) {
    return { outcome: 'undeletable', detail: 'createRecord returned no rkey' };
  }

  try {
    await deleteRecord({ repo: did, collection: TEMP_NSID, rkey });
  } catch (err) {
    return { outcome: 'undeletable', detail: `deleteRecord failed: ${err?.message || err}` };
  }

  // Verification: the ONLY truth is a follow-up listBlobs.
  try {
    const res = await listBlobs({ did, limit: 1000 });
    const cids = res?.data?.cids || [];

    if (!cids.includes(cid)) {
      return { outcome: 'deleted', detail: 'verified via listBlobs' };
    }

    return {
      outcome: 'still-present',
      detail: 'create+delete succeeded but listBlobs still contains the CID — mechanism does not work on this PDS',
    };
  } catch (err) {
    return {
      outcome: 'still-present',
      detail: `verification listBlobs failed: ${err?.message || err} — cannot confirm deletion`,
    };
  }
}

/**
 * Sweep the temp collection for strays left by interrupted runs.
 * @returns {Promise<number>} number of stray records deleted
 */
export async function sweepTempCollection({ listRecords, deleteRecord, did }) {
  let deleted = 0;
  let cursor;

  do {
    const res = await listRecords({ repo: did, collection: TEMP_NSID, limit: 100, cursor });
    const records = res?.data?.records || [];

    for (const rec of records) {
      const rkey = rec?.uri?.split('/').pop();

      if (!rkey) continue;

      try {
        await deleteRecord({ repo: did, collection: TEMP_NSID, rkey });
        deleted += 1;
      } catch {
        // ignore — next run will retry; don't let a sweep failure block the run
      }
    }

    const next = res?.data?.cursor;
    cursor = next || undefined;
  } while (cursor);

  return deleted;
}

export { RateLimitTracker };

export default cleanupOneBlob;
